import { Box } from '@chakra-ui/react';
import BirdChatPanel from '../features/bird/BirdChatPanel';
import type { BirdChatHandover } from '../../services/events/birdChatEvents';
import { NAV_RAIL_WIDTH } from './nav/navRailWidth';
import { WorkspaceSidebar } from './workspace-sidebar/WorkspaceSidebar';
import type { LeftPanel } from './useLeftPanel';

interface LeftPanelColumnProps {
  /** Opens the command palette pre-scoped to Chats, for the panel's history menu. */
  onSearchConversations?: () => void;
  /** Who holds the slot. 'none' renders nothing at all. */
  readonly panel: LeftPanel;
  /** Applied width, in px — the panels report theirs upward, this box applies it. */
  readonly width: number;
  /** Narrow viewport: the column floats over the page instead of pushing it. */
  readonly isOverlay: boolean;
  readonly onSidebarWidthChange: (width: number) => void;
  readonly onDragStateChange: (dragging: boolean) => void;
  /** Vacate the slot — every panel's own header collapse control. */
  readonly onClose: () => void;
  /**
   * A prompt handed off by another surface, plus its fresh-vs-resume decision.
   * Passed straight through to the panel, which applies it once per object
   * identity and then calls `onBirdHandoverConsumed`.
   */
  readonly birdHandover: BirdChatHandover | null;
  readonly onBirdHandoverConsumed: () => void;
  readonly onBirdWidthChange: (width: number) => void;
}

/**
 * The single viewport column immediately right of the nav rail, and whichever
 * panel currently holds it (`useLeftPanel`).
 *
 * One box for all of them on purpose: the geometry — fixed position, left offset,
 * height, overlay-vs-push z-index — is a property of the COLUMN, not of the
 * panel in it, and giving each panel its own box is how the drawer ended up
 * pushing while Bower sat inside the content flow.
 */
export function LeftPanelColumn({
  panel,
  width,
  isOverlay,
  onSidebarWidthChange,
  onDragStateChange,
  onClose,
  birdHandover,
  onBirdHandoverConsumed,
  onSearchConversations,
  onBirdWidthChange,
}: LeftPanelColumnProps) {
  if (panel === 'none') return null;

  // Bower is the one panel that goes fullscreen on a narrow viewport — the
  // sidebar keeps its own width and overlays, because its rows stay readable
  // at 352px whereas a chat transcript does not.
  const styleWidth =
    isOverlay && panel === 'bird' ? `calc(100vw - ${NAV_RAIL_WIDTH}px)` : `${width}px`;

  return (
    <Box
      data-print="hide"
      position="fixed"
      left={`${NAV_RAIL_WIDTH}px`}
      top="0"
      h={{ base: '100dvh', md: '100vh' }}
      w={styleWidth}
      zIndex={isOverlay ? 'modal' : 'base'}
      // The column is a positioning box only. Its chrome moved to the panel
      // INSIDE it (below) so the 2026-09 curved canvas has a matching inset
      // neighbour instead of a flat slab butting into it.
      //
      // `left`/`w` stay exactly as they were: Layout's `leftMargin` is derived
      // from them, so insetting the column itself would drag the content panel
      // across with it.
      bg="transparent"
    >
      {/* The visible panel. Inset 12px and curved to match the content canvas,
          which is what removes the hard seam between rail, drawer and canvas.
          `sidebar.bg` is the fill, and as of 2026-09 that token resolves to the
          SAME white the page canvas uses: once the panel became an inset card
          with a gutter and a shadow, a separate warm tone only made it read as
          a different material from the page beside it.

          NOT inset when `isOverlay`: on a narrow viewport Bower takes the full
          width beside the rail, and a floating rounded card there reads as a
          misplaced dialog rather than a panel.

          All three panels dropped their `borderRight` with this inset. It
          existed to separate a flush column from the content beside it; the
          12px gutter and the shadow do that now, and a straight edge drawn
          inside a 24px radius just clips into the corners. */}
      <Box
        // Also `hide`: this Box now sits between the column and the drawer, so
        // without it the panel is no longer covered by the column's own hook
        // and the drawer's parent stops being hidden on paper.
        data-print="hide"
        h="full"
        w="full"
        bg="sidebar.bg"
        overflow="hidden"
        {...(isOverlay
          ? {}
          : {
              // 12px on ALL FOUR sides. The first cut inset only the left
              // (`w: calc(100% - 12px)`), which left the panel's right edge
              // flush against the content canvas — two 24px-radius panels
              // touching at zero gap, measured in the browser as
              // `canvas.left - drawer.right === 0`. Every other gutter in the
              // frame is 12px; this one has to match or the drawer reads as
              // stuck to the canvas.
              // `ml`/`mr`, not `mx`: Chakra maps `mx` to `margin-inline-*`,
              // which flips under RTL. This column is positioned with a
              // physical `left` against a fixed-width rail, so a logical
              // margin here would inset the wrong edge in an RTL locale while
              // everything around it stayed put.
              mt: '12px',
              mb: '12px',
              ml: '12px',
              mr: '12px',
              h: 'calc(100% - 24px)',
              w: 'calc(100% - 24px)',
              borderRadius: '3xl',
              boxShadow: 'sm',
            })}
      >
        {panel === 'workspace' && (
          <WorkspaceSidebar
            onWidthChange={onSidebarWidthChange}
            onDragStateChange={onDragStateChange}
            onClose={onClose}
          />
        )}
        {panel === 'bird' && (
          <BirdChatPanel
            isOpen
            // 100% of the INSET card, not `100vh`. Bower's own fallback height
            // is viewport-relative (it also renders as a full-page surface on
            // mobile, where that is right), but here the card above is
            // `calc(100% - 24px)` with `overflow: hidden` — so the viewport
            // value makes the panel 24px taller than the box clipping it and
            // eats the bottom of the composer, with no scrollbar to recover
            // it. The sidebar already says `h="full"`; this is the same
            // statement, made from the side that knows the inset.
            panelHeight="100%"
            onToggle={onClose}
            onWidthChange={onBirdWidthChange}
            onDragStateChange={onDragStateChange}
            handover={birdHandover}
            onHandoverConsumed={onBirdHandoverConsumed}
            onSearchConversations={onSearchConversations}
          />
        )}
      </Box>
    </Box>
  );
}
